import L, {LatLng} from 'leaflet';
import {calculateBearing, calculateDestination, fromDegMin, toDegMin} from "../util/geoUtils";
import { encodeObjectForUrl, decodeObjectFromUrl } from '../util/urlEncoding';
import { deleteFromLocalStorage, listLocalStorageItems, saveToLocalStorage, StoredItem } from '../util/localStorage';

function calculatePositionAlongLeg(start: L.LatLng, bearing: number, speedKnots: number, timeMinutes: number): L.LatLng {
    const distanceNM = (speedKnots * timeMinutes) / 60;
    return calculateDestination(start, bearing, distanceNM);
}

function renderCoordInputGroup(
    idPrefix: string,
    label: string,
    pos: L.LatLng,
    coordFormat: 'deg-min' | 'deg-dec'
): string {
    const latDM = toDegMin(pos.lat);
    const lngDM = toDegMin(pos.lng);
    const decStyle = coordFormat === 'deg-dec' ? '' : 'display:none;';
    const dmsStyle = coordFormat === 'deg-min' ? '' : 'display:none;';

    return `
        <div style="margin-bottom: 0.5em;">
            <strong>${label}:</strong><br>
            <div style="${decStyle}">
                <div class="ppp-coord-row">
                    <span class="ppp-coord-label">Lat:</span>
                    <input type="number" id="${idPrefix}Lat" class="ppp-input" value="${pos.lat.toFixed(6)}" step="0.000001">
                </div>
                <div class="ppp-coord-row">
                    <span class="ppp-coord-label">Lng:</span>
                    <input type="number" id="${idPrefix}Lng" class="ppp-input" value="${pos.lng.toFixed(6)}" step="0.000001">
                </div>
            </div>
            <div style="${dmsStyle}">
                <div class="ppp-coord-row">
                    <span class="ppp-coord-label">Lat:</span>
                    <input type="number" class="ppp-coord-input-small ppp-modal-dms" data-target="${idPrefix}Lat" data-type="deg" value="${latDM.deg}">°
                    <input type="number" class="ppp-coord-input-small ppp-modal-dms" data-target="${idPrefix}Lat" data-type="min" value="${latDM.min.toFixed(3)}" step="0.001">'
                </div>
                <div class="ppp-coord-row">
                    <span class="ppp-coord-label">Lng:</span>
                    <input type="number" class="ppp-coord-input-small ppp-modal-dms" data-target="${idPrefix}Lng" data-type="deg" value="${lngDM.deg}">°
                    <input type="number" class="ppp-coord-input-small ppp-modal-dms" data-target="${idPrefix}Lng" data-type="min" value="${lngDM.min.toFixed(3)}" step="0.001">'
                </div>
            </div>
        </div>
    `;
}

// --- Data Models ---
interface DepthSoundingActivity { t: 'ds'; edep: number; adep?: number; cd?: number; tm?: number; }
interface RadarFixActivity { t: 'rf'; tg: string; eb: number; ed: number; ab?: number; ad?: number; tm?: number; }
interface HandCompassActivity { t: 'hc'; tg: string; eb: number; ed: number; ab?: number; ad?: number; tm?: number; }
interface TransitActivity { t: 'tr'; tg: string; eb: number; ed: number; tm?: number; }
interface BeamCheckActivity { t: 'bc'; tg: string; eb: number; ed: number; tm?: number; atm?: number; }
interface DangerBearingActivity { t: 'db'; s: 'p' | 's'; hp: number[]; dd: number; tm?: number; n?: string; }
interface ClearingBearingActivity {
    t: 'cb';
    tpos: number[];
    pb: number;
    sb: number;
    pl: number;
    sl: number;
    tm?: number;
    n?: string;
}

type Activity = DepthSoundingActivity | RadarFixActivity | HandCompassActivity | TransitActivity | BeamCheckActivity | DangerBearingActivity | ClearingBearingActivity;
interface Leg { sp: number[]; ep: number[]; s: number; ad: Activity[]; ae: Activity[]; }
interface PassagePlan { l: Leg[]; n?: string; }

// Internal models (used for runtime logic with Leaflet objects)
interface DepthSoundingActivityInternal { type: 'depthSounding'; expectedDepth: number; actualDepth?: number; chartDatum?: number; timeOffsetMinutes?: number; }
interface RadarFixActivityInternal { type: 'radarFix'; target: string; expectedBearing: number; expectedDistance: number; actualBearing?: number; actualDistance?: number; timeOffsetMinutes?: number; }
interface HandCompassActivityInternal { type: 'handCompass'; target: string; expectedBearing: number; expectedDistance: number; actualBearing?: number; actualDistance?: number; timeOffsetMinutes?: number; }
interface TransitActivityInternal { type: 'transit'; target: string; expectedBearing: number; expectedDistance: number; timeOffsetMinutes?: number; }
interface BeamCheckActivityInternal { type: 'beamCheck'; target: string; expectedBearing: number; expectedDistance: number; timeOffsetMinutes?: number; actualTimeOffsetMinutes?: number; }
interface DangerBearingActivityInternal { type: 'dangerBearing'; side: 'p' | 's'; hazardPosition: L.LatLng; dangerDistanceMeters: number; timeOffsetMinutes?: number; name?: string; }
interface ClearingBearingActivityInternal {
    type: 'clearingBearing';
    targetPosition: L.LatLng;
    portBearing: number;
    starboardBearing: number;
    portLength: number;
    starboardLength: number;
    timeOffsetMinutes?: number;
    name?: string;
}

type ActivityInternal = DepthSoundingActivityInternal | RadarFixActivityInternal | HandCompassActivityInternal | TransitActivityInternal | BeamCheckActivityInternal | DangerBearingActivityInternal | ClearingBearingActivityInternal;
interface LegInternal { startPosition: L.LatLng; endPosition: L.LatLng; speed: number; activitiesDuringLeg: ActivityInternal[]; activitiesAtEnd: ActivityInternal[]; }
interface PassagePlanInternal { legs: LegInternal[]; name?: string; }


// --- Encoding/Decoding Functions ---
function mapActivityToExternal(act: ActivityInternal): Activity {
    const base: any = {};
    if (act.timeOffsetMinutes !== undefined) base.tm = parseFloat(act.timeOffsetMinutes.toFixed(2));

    switch (act.type) {
        case 'depthSounding':
            const ds: DepthSoundingActivity = { t: 'ds', edep: act.expectedDepth, ...base };
            if (act.actualDepth !== undefined) ds.adep = act.actualDepth;
            if (act.chartDatum !== undefined) ds.cd = act.chartDatum;
            return ds;
        case 'radarFix':
            const rf: RadarFixActivity = { t: 'rf', tg: act.target, eb: parseFloat(act.expectedBearing.toFixed(1)), ed: parseFloat(act.expectedDistance.toFixed(2)), ...base };
            if (act.actualBearing !== undefined) rf.ab = parseFloat(act.actualBearing.toFixed(1));
            if (act.actualDistance !== undefined) rf.ad = parseFloat(act.actualDistance.toFixed(2));
            return rf;
        case 'handCompass':
            const hc: HandCompassActivity = { t: 'hc', tg: act.target, eb: parseFloat(act.expectedBearing.toFixed(1)), ed: parseFloat(act.expectedDistance.toFixed(2)), ...base };
            if (act.actualBearing !== undefined) hc.ab = parseFloat(act.actualBearing.toFixed(1));
            if (act.actualDistance !== undefined) hc.ad = parseFloat(act.actualDistance.toFixed(2));
            return hc;
        case 'transit':
            return { t: 'tr', tg: act.target, eb: parseFloat(act.expectedBearing.toFixed(1)), ed: parseFloat(act.expectedDistance.toFixed(2)), ...base };
        case 'beamCheck':
            const bc: BeamCheckActivity = { t: 'bc', tg: act.target, eb: parseFloat(act.expectedBearing.toFixed(1)), ed: parseFloat(act.expectedDistance.toFixed(2)), ...base };
            if (act.actualTimeOffsetMinutes !== undefined) bc.atm = parseFloat(act.actualTimeOffsetMinutes.toFixed(2));
            return bc;
        case 'dangerBearing':
            const db: DangerBearingActivity = {
                t: 'db',
                s: act.side,
                hp: [parseFloat(act.hazardPosition.lat.toFixed(5)), parseFloat(act.hazardPosition.lng.toFixed(5))],
                dd: Math.round(act.dangerDistanceMeters),
                ...base
            };
            if (act.name) db.n = act.name;
            return db;
        case 'clearingBearing':
            const cb: ClearingBearingActivity = {
                t: 'cb',
                tpos: [parseFloat(act.targetPosition.lat.toFixed(5)), parseFloat(act.targetPosition.lng.toFixed(5))],
                pb: parseFloat(act.portBearing.toFixed(1)),
                sb: parseFloat(act.starboardBearing.toFixed(1)),
                pl: parseFloat(act.portLength.toFixed(2)),
                sl: parseFloat(act.starboardLength.toFixed(2)),
                ...base
            };
            if (act.name) cb.n = act.name;
            return cb;
    }
    throw new Error(`Unknown activity type: ${(act as any).type}`);
}

function mapActivityToInternal(act: any): ActivityInternal {
    const base: any = {};
    const tm = act.tm !== undefined ? act.tm : act.timeOffsetMinutes;
    if (tm !== undefined) base.timeOffsetMinutes = tm;

    // Support both short (new) and long (old) property names for backward compatibility
    const type = act.t || act.type;

    switch (type) {
        case 'ds':
        case 'depthSounding':
            const ds: DepthSoundingActivityInternal = { 
                type: 'depthSounding', 
                expectedDepth: act.edep !== undefined ? act.edep : act.expectedDepth, 
                ...base 
            };
            const adep = act.adep !== undefined ? act.adep : act.actualDepth;
            if (adep !== undefined) ds.actualDepth = adep;
            const cd = act.cd !== undefined ? act.cd : act.chartDatum;
            if (cd !== undefined) ds.chartDatum = cd;
            return ds;
        case 'rf':
        case 'radarFix':
            const rf: RadarFixActivityInternal = { 
                type: 'radarFix', 
                target: act.tg || act.target, 
                expectedBearing: act.eb !== undefined ? act.eb : act.expectedBearing, 
                expectedDistance: act.ed !== undefined ? act.ed : act.expectedDistance, 
                ...base 
            };
            const abRf = act.ab !== undefined ? act.ab : act.actualBearing;
            if (abRf !== undefined) rf.actualBearing = abRf;
            const adRf = act.ad !== undefined ? act.ad : act.actualDistance;
            if (adRf !== undefined) rf.actualDistance = adRf;
            return rf;
        case 'hc':
        case 'handCompass':
            const hc: HandCompassActivityInternal = { 
                type: 'handCompass', 
                target: act.tg || act.target, 
                expectedBearing: act.eb !== undefined ? act.eb : act.expectedBearing, 
                expectedDistance: act.ed !== undefined ? act.ed : act.expectedDistance, 
                ...base 
            };
            const abHc = act.ab !== undefined ? act.ab : act.actualBearing;
            if (abHc !== undefined) hc.actualBearing = abHc;
            const adHc = act.ad !== undefined ? act.ad : act.actualDistance;
            if (adHc !== undefined) hc.actualDistance = adHc;
            return hc;
        case 'tr':
        case 'transit':
            return { 
                type: 'transit', 
                target: act.tg || act.target, 
                expectedBearing: act.eb !== undefined ? act.eb : act.expectedBearing, 
                expectedDistance: act.ed !== undefined ? act.ed : act.expectedDistance, 
                ...base 
            };
        case 'bc':
        case 'beamCheck':
            const bc: BeamCheckActivityInternal = { 
                type: 'beamCheck', 
                target: act.tg || act.target, 
                expectedBearing: act.eb !== undefined ? act.eb : act.expectedBearing, 
                expectedDistance: act.ed !== undefined ? act.ed : act.expectedDistance, 
                ...base 
            };
            const atm = act.atm !== undefined ? act.atm : act.actualTimeOffsetMinutes;
            if (atm !== undefined) bc.actualTimeOffsetMinutes = atm;
            return bc;
        case 'db':
        case 'dangerBearing':
            // Check for position in short (hp) or long (hazardPosition) format
            let hpLat, hpLng;
            if (act.hp) {
                hpLat = act.hp[0];
                hpLng = act.hp[1];
            } else if (act.hazardPosition) {
                hpLat = act.hazardPosition.lat;
                hpLng = act.hazardPosition.lng;
            } else {
                // Fallback default
                hpLat = 0; hpLng = 0; 
            }

            const db: DangerBearingActivityInternal = {
                type: 'dangerBearing',
                side: act.s || act.side,
                hazardPosition: new L.LatLng(hpLat, hpLng),
                dangerDistanceMeters: act.dd !== undefined ? act.dd : act.dangerDistanceMeters,
                ...base
            };
            const nDb = act.n || act.name;
            if (nDb) db.name = nDb;
            return db;
        case 'cb':
        case 'clearingBearing':
            let tpLat, tpLng;
            if (act.tpos) {
                tpLat = act.tpos[0];
                tpLng = act.tpos[1];
            } else if (act.targetPosition) {
                tpLat = act.targetPosition.lat;
                tpLng = act.targetPosition.lng;
            } else {
                tpLat = 0; tpLng = 0;
            }

            const cb: ClearingBearingActivityInternal = {
                type: 'clearingBearing',
                targetPosition: new L.LatLng(tpLat, tpLng),
                portBearing: act.pb !== undefined ? act.pb : act.portBearing,
                starboardBearing: act.sb !== undefined ? act.sb : act.starboardBearing,
                portLength: act.pl !== undefined ? act.pl : act.portLength,
                starboardLength: act.sl !== undefined ? act.sl : act.starboardLength,
                ...base
            };
            const nCb = act.n || act.name;
            if (nCb) cb.name = nCb;
            return cb;
    }
    // @ts-ignore
    throw new Error(`Unknown activity type: ${type}`);
}

function mapPlanToExternal(plan: PassagePlanInternal): PassagePlan {
    return {
        l: plan.legs.map(leg => ({
            sp: [parseFloat(leg.startPosition.lat.toFixed(5)), parseFloat(leg.startPosition.lng.toFixed(5))],
            ep: [parseFloat(leg.endPosition.lat.toFixed(5)), parseFloat(leg.endPosition.lng.toFixed(5))],
            s: leg.speed,
            ad: leg.activitiesDuringLeg.map(mapActivityToExternal),
            ae: leg.activitiesAtEnd.map(mapActivityToExternal)
        })),
        n: plan.name
    };
}

function mapPlanToInternal(plan: any): PassagePlanInternal {
    // Handle both short 'l' and long 'legs' properties
    const legsData = plan.l || plan.legs || [];
    
    return {
        legs: legsData.map((leg: any) => {
            // Handle coordinates: sp/ep (array) vs startPosition/endPosition (object)
            let startLat, startLng, endLat, endLng;
            
            if (leg.sp) {
                startLat = leg.sp[0];
                startLng = leg.sp[1];
            } else if (leg.startPosition) {
                startLat = leg.startPosition.lat;
                startLng = leg.startPosition.lng;
            } else {
                startLat = 0; startLng = 0;
            }

            if (leg.ep) {
                endLat = leg.ep[0];
                endLng = leg.ep[1];
            } else if (leg.endPosition) {
                endLat = leg.endPosition.lat;
                endLng = leg.endPosition.lng;
            } else {
                endLat = 0; endLng = 0;
            }

            return {
                startPosition: new L.LatLng(startLat, startLng),
                endPosition: new L.LatLng(endLat, endLng),
                speed: leg.s !== undefined ? leg.s : leg.speed,
                activitiesDuringLeg: (leg.ad || leg.activitiesDuringLeg || []).map(mapActivityToInternal),
                activitiesAtEnd: (leg.ae || leg.activitiesAtEnd || []).map(mapActivityToInternal)
            };
        }),
        name: plan.n || plan.name
    };
}


function encodePassagePlan(plan: PassagePlanInternal): string {
    const externalPlan = mapPlanToExternal(plan);
    return encodeObjectForUrl(externalPlan);
}

function decodePassagePlan(encoded: string): PassagePlanInternal | null {
    const planObj = decodeObjectFromUrl(encoded);
    // Logic updated to support legacy long names if present
    if (planObj && (Array.isArray(planObj.l) || Array.isArray(planObj.legs))) {
        return mapPlanToInternal(planObj);
    }
    return null;
}

export function initPassagePlanner(
    map: L.Map,
    measureControl: any,
    initialPlanString?: string,
    onPlanChange?: (encodedPlan: string) => void
) {
    // 1. Inject CSS for Printing and Icons
    const style = document.createElement('style');
    style.innerHTML = `
        .ppp-waypoint-icon {
            background-color: white;
            border: 2px solid blue;
            border-radius: 50%;
            text-align: center;
            line-height: 20px;
            font-weight: bold;
            font-size: 12px;
            color: blue;
        }
        #passage-planner-panel {
            color: #333;
            color-scheme: light;
        }
        #passage-planner-panel input, #passage-planner-panel select, #passage-planner-panel button {
            color: #333;
            background-color: #fff;
            border: 1px solid #ccc;
        }
        /* Hide spinners for number inputs */
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        input[type="number"] {
            -moz-appearance: textfield; /* Firefox */
            appearance: textfield; /* Standard property */
        }

        #passage-planner-panel .ppp-coord-input {
            width: 100px;
            padding: 2px;
            margin-right: 2px;
            margin-left: 0;
        }
        .ppp-coord-input-small {
            padding: 1px;
            margin-right: 1px;
            margin-left: 0;
        }
        .ppp-dms[data-sub$="Deg"] {
            width: 35px;
        }
        .ppp-dms[data-sub$="Min"] {
            width: 55px;
        }
        .ppp-coord-row {
            display: flex;
            align-items: center;
            white-space: nowrap;
            margin-bottom: 2px;
        }
        .ppp-coord-label {
            width: 25px;
            display: inline-block;
            margin-right: 5px; /* Added margin for gap */
        }
        .ppp-coord-cell {
            width: 1%;
            white-space: nowrap;
        }
        .ppp-details-cell {
            min-width: 120px;
        }
        .ppp-activities-cell {
            min-width: 150px;
        }
        @media (max-width: 600px) {
            #passage-planner-panel {
                left: 50px !important;
                right: 5px !important;
                width: auto !important;
                min-width: 0 !important;
            }
            .ppp-details-cell, .ppp-activities-cell {
                min-width: auto;
            }
        }
        @media print {
            body * {
                visibility: hidden;
            }
            #passage-planner-panel, #passage-planner-panel * {
                visibility: visible;
            }
            #passage-planner-panel {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                max-width: none !important;
                max-height: none !important;
                overflow: visible !important;
                background: white;
                border: none;
                box-shadow: none;
                color: black;
            }
            /* Hide UI controls that aren't needed on paper */
            #ppp-add-leg-btn,
            #ppp-import-measure-btn,
            #ppp-close-btn,
            #ppp-print-btn,
            #ppp-clear-btn,
            #ppp-show-btn,
            .ppp-remove-leg-btn,
            .ppp-split-leg-btn,
            .ppp-add-activity-btn,
            .ppp-remove-activity-btn,
            .leaflet-control-zoom,
            .leaflet-control-attribution {
                display: none !important;
            }
            /* Hide the Actions column in the table */
            .passage-plan-table th:last-child,
            .passage-plan-table td:last-child {
                display: none;
            }
            /* Ensure text is black */
            body {
                color: black;
            }
        }
    `;
    document.head.appendChild(style);

    // 2. Inject HTML
    const panelHtml = `
        <div id="passage-planner-panel" class="leaflet-control leaflet-bar" style="display:none; background-color: rgba(255, 255, 255, 0.95); color: #333; padding: 1em; width: auto; max-width: 95%; min-width: 300px; margin: 0; overflow: auto; max-height: 80vh; position: absolute; top: 50px; left: 60px; right: 10px; z-index: 1000; cursor: grab; resize: both;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1em; border-bottom: 2px solid #333; padding-bottom: 0.5em;">
                <div style="font-weight: bold; font-size: 1.2em; color: #555;">seamap.co.nz</div>
                <h3 style="margin: 0; cursor: grabbing; flex-grow: 1; text-align: center; color: #333;">Passage Planner</h3>
                <input type="text" id="ppp-plan-name" placeholder="Name and Description" style="font-weight: bold; font-size: 1.2em; color: #555; width: 250px; text-align: right;" />
            </div>
            
            <section id="ppp-saved-section" style="margin-bottom: 1em; padding: 0.5em; border: 1px solid #ccc; border-radius: 4px; display: none;">
                <h4 style="margin-top: 0; margin-bottom: 0.5em;">Saved Plans</h4>
                <div id="ppp-saved-list" style="max-height: 100px; overflow-y: auto;"></div>
            </section>

            <div style="border: 1px solid #ccc; padding: 0.5em; margin-bottom: 1em; border-radius: 4px; display: flex; flex-wrap: wrap; gap: 1.5em; align-items: center; background: #f9f9f9; color: #333;">
                <div style="flex-grow: 1; display: flex; gap: 1.5em; align-items: center;">
                    <div>
                        <strong>Coords:</strong>
                        <label><input type="radio" name="ppp-coord-format" value="deg-min" checked> Deg Min</label>
                        <label><input type="radio" name="ppp-coord-format" value="deg-dec"> Decimal</label>
                    </div>
                    <div>
                        <strong>Bearings:</strong>
                        <label><input type="radio" name="ppp-bearing-ref" value="true"> True</label>
                        <label><input type="radio" name="ppp-bearing-ref" value="mag" checked> Magnetic</label>
                    </div>
                    <div>
                        <label>Variation: <input type="number" id="ppp-mag-var" value="20" style="width: 50px;">°E</label>
                    </div>
                </div>
                <div style="margin-left: auto; font-weight: bold; color: #555;">
                    Total Dist: <span id="ppp-total-dist">0.00</span> NM | Total Time: <span id="ppp-total-time">0</span> mins
                </div>
            </div>

            <div id="passage-plan-table-container"></div>
            <div style="margin-top:1em; display: flex; justify-content: space-between;">
                <div>
                    <button id="ppp-add-leg-btn">Add Leg</button>
                    <button id="ppp-import-measure-btn" title="Create plan from the last finished measurement">Import from Measure</button>
                    <button id="ppp-save-btn">💾 Save</button>
                    <button id="ppp-show-btn">Show</button>
                    <button id="ppp-clear-btn">Clear</button>
                </div>
                <div>
                    <button id="ppp-print-btn">Print</button>
                    <button id="ppp-close-btn">Close</button>
                </div>
            </div>
        </div>
        <div id="ppp-activity-modal" style="display:none; position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; color: #333; padding: 1em; border: 1px solid #333; z-index: 1002; box-shadow: 0 5px 15px rgba(0,0,0,0.5); max-height: 80vh; overflow-y: auto; width: 90%; max-width: 400px; box-sizing: border-box;">
            <h4 style="color: #333;">Add/Edit Activity</h4>
            <div id="ppp-activity-form-container"></div>
            <div style="margin-top: 1em; display: flex; justify-content: flex-end;">
                <button id="ppp-cancel-activity-btn" style="margin-right: 0.5em;">Cancel</button>
                <button id="ppp-save-activity-btn">Save</button>
            </div>
        </div>
    `;

    const appContainer = document.getElementById('app');
    if (appContainer) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = panelHtml;
        while (tempDiv.firstChild) {
            appContainer.appendChild(tempDiv.firstChild);
        }
    } else {
        console.error("App container not found!");
        return;
    }

    // Draggable Panel Logic
    const panelElement = document.getElementById('passage-planner-panel')!;
    let isDragging = false;
    let offsetX = 0, offsetY = 0;
    const header = panelElement.querySelector('h3')!;
    header.addEventListener('mousedown', (e: MouseEvent) => {
        isDragging = true;
        offsetX = e.clientX - panelElement.getBoundingClientRect().left;
        offsetY = e.clientY - panelElement.getBoundingClientRect().top;
    });
    document.addEventListener('mousemove', (e: MouseEvent) => {
        if (isDragging) {
            panelElement.style.left = `${e.clientX - offsetX}px`;
            panelElement.style.top = `${e.clientY - offsetY}px`;
        }
    });
    document.addEventListener('mouseup', () => { isDragging = false; });

    // 2. Get References
    const passagePlannerPanel = document.getElementById('passage-planner-panel')!;
    const closeBtn = document.getElementById('ppp-close-btn')!;
    const printBtn = document.getElementById('ppp-print-btn')!;
    const addLegBtn = document.getElementById('ppp-add-leg-btn')!;
    const importMeasureBtn = document.getElementById('ppp-import-measure-btn')! as HTMLButtonElement;
    const saveBtn = document.getElementById('ppp-save-btn')!;
    const clearBtn = document.getElementById('ppp-clear-btn')!;
    const showBtn = document.getElementById('ppp-show-btn')!;
    const tableContainer = document.getElementById('passage-plan-table-container')!;
    const activityModal = document.getElementById('ppp-activity-modal')!;
    const activityFormContainer = document.getElementById('ppp-activity-form-container')!;
    const saveActivityBtn = document.getElementById('ppp-save-activity-btn')!;
    const cancelActivityBtn = document.getElementById('ppp-cancel-activity-btn')!;
    const planNameInput = document.getElementById('ppp-plan-name') as HTMLInputElement;
    const totalDistSpan = document.getElementById('ppp-total-dist')!;
    const totalTimeSpan = document.getElementById('ppp-total-time')!;
    const savedSection = document.getElementById('ppp-saved-section')!;
    const savedList = document.getElementById('ppp-saved-list')!;

    const coordFormatRadios = document.querySelectorAll('input[name="ppp-coord-format"]');
    const bearingRefRadios = document.querySelectorAll('input[name="ppp-bearing-ref"]');
    const magVarInput = document.getElementById('ppp-mag-var') as HTMLInputElement;

    // --- Data & State ---
    let passagePlan: PassagePlanInternal = { legs: [] };
    let coordFormat: 'deg-min' | 'deg-dec' = 'deg-min';
    let bearingRef: 'true' | 'mag' = 'mag';
    let magVariation = 20;

    // Load initial plan if provided
    if (initialPlanString) {
        const decodedPlan = decodePassagePlan(initialPlanString);
        if (decodedPlan) {
            passagePlan = decodedPlan;
        }
    }

    if (passagePlan.name) {
        planNameInput.value = passagePlan.name;
    }

    let lastMeasuredPolyline: L.LatLng[] | null = null;
    const routeLayerGroup = L.featureGroup().addTo(map);
    let editingActivity: { legIndex: number; position: 'activitiesDuringLeg' | 'activitiesAtEnd'; activityIndex?: number } | null = null;

    // --- Core Logic ---
    function render() {
        renderTable();
        updateMapVisualization();
        updateTotals();
        renderSavedPlans();

        if (onPlanChange) {
            const encoded = encodePassagePlan(passagePlan);
            onPlanChange(encoded);
        }
    }

    function updateTotals() {
        let totalDist = 0;
        let totalTime = 0;

        passagePlan.legs.forEach(leg => {
            const dist = leg.startPosition.distanceTo(leg.endPosition) / 1852;
            totalDist += dist;
            if (leg.speed > 0) {
                totalTime += (dist / leg.speed) * 60;
            }
        });

        totalDistSpan.innerText = totalDist.toFixed(2);
        totalTimeSpan.innerText = totalTime.toFixed(0);
    }

    function togglePassagePlanner() {
        const isVisible = passagePlannerPanel.style.display === 'block';
        passagePlannerPanel.style.display = isVisible ? 'none' : 'block';
        if (!isVisible) {
            map.getContainer().dispatchEvent(new CustomEvent('close-other-panels', { detail: 'passage-planner-panel' }));
            render();
        }
    }

    function formatCoord(pos: L.LatLng): string {
        if (coordFormat === 'deg-dec') {
            return `${pos.lat.toFixed(6)}<br>\${pos.lng.toFixed(6)}`;
        } else {
            const lat = toDegMin(pos.lat);
            const lng = toDegMin(pos.lng);
            return `${lat.deg}° ${lat.min.toFixed(3)}'<br>${lng.deg}° ${lng.min.toFixed(3)}'`;
        }
    }

    function updateMapVisualization() {
        routeLayerGroup.clearLayers();
        if (passagePlan.legs.length === 0) return;

        const plannedPath: L.LatLng[] = [];

        passagePlan.legs.forEach((leg, index) => {
            // Start Marker
            if (index === 0) {
                const startMarker = L.marker(leg.startPosition, {
                    draggable: true,
                    icon: L.divIcon({
                        className: 'ppp-waypoint-icon',
                        html: '0',
                        iconSize: [20, 20],
                        iconAnchor: [10, 10]
                    })
                }).bindPopup(formatCoord(leg.startPosition))
                    .addTo(routeLayerGroup);

                startMarker.on('dragend', (e) => {
                    passagePlan.legs[0].startPosition = (e.target as L.Marker).getLatLng();
                    render();
                });
                plannedPath.push(leg.startPosition);
            }

            // End Marker (Waypoint)
            const wpIndex = index + 1;
            const hasDepth = leg.activitiesAtEnd.some(a => a.type === 'depthSounding');
            const endMarker = L.marker(leg.endPosition, {
                draggable: true,
                icon: L.divIcon({
                    className: 'ppp-waypoint-icon',
                    html: hasDepth ? `<u>${wpIndex.toString()}</u>` :  wpIndex.toString(),
                    iconSize: [20, 20],
                    iconAnchor: [10, 10]
                })
            }).bindPopup(formatCoord(leg.startPosition))
                .addTo(routeLayerGroup);

            endMarker.on('dragend', (e) => {
                const newPos = (e.target as L.Marker).getLatLng();
                passagePlan.legs[index].endPosition = newPos;
                if (index + 1 < passagePlan.legs.length) {
                    passagePlan.legs[index + 1].startPosition = newPos;
                }
                render();
            });

            plannedPath.push(leg.endPosition);

            leg.activitiesAtEnd.forEach((act, actIndex) => visualizeActivity(act, leg.endPosition, index, 'activitiesAtEnd', actIndex));
            leg.activitiesDuringLeg.forEach((act, actIndex) => {
                if (act.timeOffsetMinutes !== undefined) {
                    const bearing = calculateBearing(leg.startPosition, leg.endPosition);
                    const origin = calculatePositionAlongLeg(leg.startPosition, bearing, leg.speed, act.timeOffsetMinutes);
                    visualizeActivity(act, origin, index, 'activitiesDuringLeg', actIndex);
                }
            });
        });

        L.polyline(plannedPath, { color: 'blue', weight: 4, opacity: 0.7, dashArray: '5, 10' }).addTo(routeLayerGroup);
    }

    function visualizeActivity(act: ActivityInternal, origin: L.LatLng, legIndex: number, position: 'activitiesDuringLeg' | 'activitiesAtEnd', actIndex: number) {
        let markerIcon: string;
        let lineColor: string;

        switch (act.type) {
            case 'depthSounding':
                markerIcon = '🎯';
                lineColor = 'blue';
                break;
            case 'radarFix':
                markerIcon = '🟣'; // purple circle
                lineColor = 'purple';
                break;
            case 'handCompass':
                markerIcon = '🟠'; // orange circle
                lineColor = 'orange';
                break;
            case 'transit':
                markerIcon = '👀'
                lineColor = 'yellow';
                break;
            case 'clearingBearing':
                markerIcon = '🔴🌲';
                lineColor = 'red|green';
                break;
            case 'dangerBearing':
                markerIcon = '⚠️';
                lineColor = 'red|green';
                break;
            default :
                markerIcon = '🎯';
                lineColor = 'blue';
                break;
        }
        if (act.type === 'clearingBearing') {
            // Draw Target
            const targetMarker = L.marker(act.targetPosition, {
                draggable: true,
                icon: L.divIcon({ className: 'activity-target-icon', html: '◎', iconSize: [20, 20], iconAnchor: [10, 10] })
            }).addTo(routeLayerGroup);

            targetMarker.on('dragend', (e) => {
                const newPos = (e.target as L.Marker).getLatLng();
                const activityToUpdate = passagePlan.legs[legIndex][position][actIndex] as ClearingBearingActivityInternal;
                activityToUpdate.targetPosition = newPos;
                render();
            });

            // Draw Bearing Lines
            const drawLine = (bearing: number, length: number, isPort: boolean) => {
                const endPos = calculateDestination(act.targetPosition, bearing, length);
                const [portColor, starboardColor] = lineColor.split('|');
                const color = isPort ? portColor : starboardColor;

                L.polyline([act.targetPosition, endPos], { color: color, dashArray: '5, 5' }).addTo(routeLayerGroup);

                const endMarker = L.marker(endPos, {
                    draggable: true,
                    icon: L.divIcon({ className: 'activity-target-icon', html: [...markerIcon][isPort ? 0 : 1], iconSize: [16, 16], iconAnchor: [8, 8] })
                })

                const displayBearing = bearingRef === 'mag' ? (bearing - magVariation + 360) % 360 : bearing;
                const bearingSuffix = bearingRef === 'mag' ? '°M' : '°T';
                const bearingType = bearingRef === 'mag' ? 'magnetic' : 'true';
                const tipString = `${displayBearing.toFixed(0)}${bearingSuffix} ${bearingType}`;
                endMarker
                    .bindTooltip(`${act.type}: ${act.name}<br>${tipString}`)
                    .addTo(routeLayerGroup);

                endMarker.on('dragend', (e) => {
                    const newEnd = (e.target as L.Marker).getLatLng();
                    const newBearing = calculateBearing(act.targetPosition, newEnd);
                    const newLength = act.targetPosition.distanceTo(newEnd) / 1852;

                    const activityToUpdate = passagePlan.legs[legIndex][position][actIndex] as ClearingBearingActivityInternal;
                    if (isPort) {
                        activityToUpdate.portBearing = newBearing;
                        activityToUpdate.portLength = parseFloat(newLength.toFixed(2));
                    } else {
                        activityToUpdate.starboardBearing = newBearing;
                        activityToUpdate.starboardLength = parseFloat(newLength.toFixed(2));
                    }
                    render();
                });
            };

            if (act.portBearing !== undefined) drawLine(act.portBearing, act.portLength || 1, true);
            if (act.starboardBearing !== undefined) drawLine(act.starboardBearing, act.starboardLength || 1, false);

            return;
        }

        if (act.type === 'dangerBearing') {
            // Draw Hazard Circle
            L.circle(act.hazardPosition, {
                radius: act.dangerDistanceMeters,
                color: act.side === 'p' ? 'red' : 'green',
                fillOpacity: 0.2,
                weight: 2
            }).addTo(routeLayerGroup);

            // Draggable Hazard Marker
            const hazardMarker = L.marker(act.hazardPosition, {
                draggable: true,
                icon: L.divIcon({ className: 'activity-target-icon', html: markerIcon, iconSize: [20, 20], iconAnchor: [10, 10] })
            }).addTo(routeLayerGroup);

            hazardMarker.on('dragend', (e) => {
                const newPos = (e.target as L.Marker).getLatLng();
                const activityToUpdate = passagePlan.legs[legIndex][position][actIndex] as DangerBearingActivityInternal;
                activityToUpdate.hazardPosition = newPos;
                render();
            });

            // Calculate Tangent Bearing
            const distMeters = origin.distanceTo(act.hazardPosition);
            if (distMeters > act.dangerDistanceMeters) {
                const bearingToHazard = calculateBearing(origin, act.hazardPosition);
                const alphaRad = Math.asin(act.dangerDistanceMeters / distMeters);
                const alphaDeg = alphaRad * 180 / Math.PI;

                let tangentBearing: number;
                if (act.side === 'p') {
                    // Port hazard -> Keep hazard on Left -> We are to the Right -> Use Right Tangent
                    tangentBearing = (bearingToHazard + alphaDeg) % 360;
                } else {
                    // Starboard hazard -> Keep hazard on Right -> We are to the Left -> Use Left Tangent
                    tangentBearing = (bearingToHazard - alphaDeg + 360) % 360;
                }

                // Draw Tangent Line
                const tangentLengthMeters = Math.sqrt(distMeters * distMeters - act.dangerDistanceMeters * act.dangerDistanceMeters);
                const tangentLengthNM = tangentLengthMeters / 1852;
                const tangentPoint = calculateDestination(origin, tangentBearing, tangentLengthNM);
                const [portColor, starboardColor] = lineColor.split('|');
                const color = act.side === 'p' ? portColor : starboardColor;

                L.polyline([origin, tangentPoint], {
                    color: color,
                    weight: 2,
                    dashArray: '5, 5'
                }).addTo(routeLayerGroup);

                // Add tooltip to the line
                const displayBearing = bearingRef === 'mag' ? (tangentBearing - magVariation + 360) % 360 : tangentBearing;
                const bearingSuffix = bearingRef === 'mag' ? '°M' : '°T';
                const bearingType = bearingRef === 'mag' ? 'magnetic' : 'true';
                const limitText = act.side === 'p' ? 'NMT' : 'NLT';
                const tipString = `${act.name || 'Danger'}: ${limitText} ${displayBearing.toFixed(0)}${bearingSuffix} ${bearingType}`;

                // Add a marker at the end of the tangent line to hold the tooltip
                const endMarker = L.marker(tangentPoint, {
                    icon: L.divIcon({ className: 'activity-target-icon', html: markerIcon, iconSize: [16, 16], iconAnchor: [8, 8] })
                }).addTo(routeLayerGroup);

                endMarker.bindTooltip(tipString);
            }
            return;
        }

        if (act.type !== 'radarFix' && act.type !== 'handCompass' && act.type !== 'transit' && act.type !== 'beamCheck') return;

        // Calculate bearing for Beam Check
        let bearingToUse = act.expectedBearing;
        if (act.type === 'beamCheck') {
             const leg = passagePlan.legs[legIndex];
             const legBearing = calculateBearing(leg.startPosition, leg.endPosition);
             bearingToUse = (legBearing + act.expectedBearing) % 360;
        }

        // 1. Draw Expected Line (Dashed)
        const dest = calculateDestination(origin, bearingToUse, act.expectedDistance);

        const beamLine = L.polyline([origin, dest], { color: lineColor, weight: 4, dashArray: '5, 10' }).addTo(routeLayerGroup);

        // Draw draggable target marker
        const marker = L.marker(dest, {
            draggable: true,
            icon: L.divIcon({ className: 'activity-target-icon', html: markerIcon, iconSize: [20, 20], iconAnchor: [10, 10] })
        }).addTo(routeLayerGroup);

        let bearingStr: string;
        if (act.type === 'beamCheck') {
            bearingStr = `${act.expectedBearing.toFixed(0)}°Rel relative`;
        } else {
            const displayBearing = bearingRef === 'mag' ? (act.expectedBearing - magVariation + 360) % 360 : act.expectedBearing;
            const bearingSuffix = bearingRef === 'mag' ? '°M' : '°T';
            const bearingType = bearingRef === 'mag' ? 'magnetic' : 'true';
            bearingStr = `${displayBearing.toFixed(0)}${bearingSuffix} ${bearingType}`;
            if (act.type === 'radarFix') {
                bearingStr = `${bearingStr}<br>${act.expectedDistance.toFixed(2)} NM`;
            }
        }
        marker.bindTooltip(`${act.type}: ${act.target}<br>${bearingStr}`);

        marker.on('dragend', (e) => {
            const newDest = (e.target as L.Marker).getLatLng();
            const activityToUpdate = passagePlan.legs[legIndex][position][actIndex] as BeamCheckActivityInternal | RadarFixActivityInternal | HandCompassActivityInternal | TransitActivityInternal;

            if (activityToUpdate.type === 'beamCheck') {
                // For beam check, dragging the target only changes the distance, keeping the angle constant.
                const newDistance = origin.distanceTo(newDest) / 1852;
                activityToUpdate.expectedDistance = parseFloat(newDistance.toFixed(2));
            } else {
                // For other types, update both bearing and distance.
                const newBearing = calculateBearing(origin, newDest);
                const newDistance = origin.distanceTo(newDest) / 1852;
                activityToUpdate.expectedBearing = parseFloat(newBearing.toFixed(1));
                activityToUpdate.expectedDistance = parseFloat(newDistance.toFixed(2));
            }
            render();
        });

        // For 'beamCheck' during a leg, add a slider to move it along the leg.
        if (act.type === 'beamCheck' && position === 'activitiesDuringLeg') {
            const midPoint = L.latLng((origin.lat + dest.lat) / 2, (origin.lng + dest.lng) / 2);
            const slider = L.marker(midPoint, {
                draggable: true,
                icon: L.divIcon({ className: 'activity-target-icon', html: '↔️', iconSize: [20, 20], iconAnchor: [10, 10] })
            }).addTo(routeLayerGroup);

            slider.on('drag', (e) => {
                const leg = passagePlan.legs[legIndex];
                const p1 = map.latLngToContainerPoint(leg.startPosition);
                const p2 = map.latLngToContainerPoint(leg.endPosition);
                const p = map.latLngToContainerPoint((e.target as L.Marker).getLatLng());

                // Project p onto line segment p1-p2
                const vx = p2.x - p1.x;
                const vy = p2.y - p1.y;
                const len2 = vx * vx + vy * vy;

                let t = 0;
                if (len2 !== 0) {
                    const wx = p.x - p1.x;
                    const wy = p.y - p1.y;
                    t = (wx * vx + wy * vy) / len2;
                }

                // Clamp t to [0, 1]
                t = Math.max(0, Math.min(1, t));

                // Calculate new time offset
                const distanceNm = leg.startPosition.distanceTo(leg.endPosition) / 1852;
                if (leg.speed > 0) {
                    const totalMinutes = (distanceNm / leg.speed) * 60;
                    const newTimeOffset = t * totalMinutes;

                    // Update model
                    const activityToUpdate = passagePlan.legs[legIndex][position][actIndex] as BeamCheckActivityInternal;
                    activityToUpdate.timeOffsetMinutes = parseFloat(newTimeOffset.toFixed(2));

                    // Update Visualization
                    const bearing = calculateBearing(leg.startPosition, leg.endPosition);
                    const newOrigin = calculatePositionAlongLeg(leg.startPosition, bearing, leg.speed, newTimeOffset);

                    // Recalculate destination based on relative bearing
                    // bearingToUse is already calculated in the outer scope
                    const newDest = calculateDestination(newOrigin, bearingToUse, act.expectedDistance);

                    // Update elements
                    beamLine.setLatLngs([newOrigin, newDest]);
                    marker.setLatLng(newDest);

                    // Update slider position to new midpoint
                    const newMidPoint = L.latLng((newOrigin.lat + newDest.lat) / 2, (newOrigin.lng + newDest.lng) / 2);
                    slider.setLatLng(newMidPoint);
                }
            });

            slider.on('dragend', () => {
                render(); // Do a full re-render on drag end to persist the final state.
            });
        }

        // 2. Draw Actual Line (Solid) if actual values exist (Transit has no actuals)
        if (act.type !== 'transit' && act.type !== 'beamCheck' && act.actualBearing !== undefined) {
            // Calculate reciprocal bearing
            const reciprocalBearing = (act.actualBearing + 180) % 360;

            if (act.actualDistance !== undefined) {
                const actualLineEnd = calculateDestination(dest, reciprocalBearing, act.actualDistance);
                L.polyline([dest, actualLineEnd], { color: lineColor, weight: 4 }).addTo(routeLayerGroup);
            }
        }

        // 3. Draw Actual Line (Solid) for Beam Check if actual time offset exists
        if (act.type === 'beamCheck' && act.actualTimeOffsetMinutes !== undefined) {
            const leg = passagePlan.legs[legIndex];
            const bearing = calculateBearing(leg.startPosition, leg.endPosition);
            const actualOrigin = calculatePositionAlongLeg(leg.startPosition, bearing, leg.speed, act.actualTimeOffsetMinutes);

            const actualDest = calculateDestination(actualOrigin, bearingToUse, act.expectedDistance);
            L.polyline([actualOrigin, actualDest], { color: lineColor, weight: 4 }).addTo(routeLayerGroup);
        }
    }

    function renderTable() {
        let tableHtml = `
            <table class="passage-plan-table" style="width: 100%; border-collapse: collapse;">
                <thead>
                    <tr style="background-color: #f2f2f2;">
                        <th class="ppp-coord-cell" style="border: 1px solid #ddd; padding: 8px;">Start Coords</th>
                        <th class="ppp-details-cell" style="border: 1px solid #ddd; padding: 8px;">Leg Details</th>
                        <th class="ppp-activities-cell" style="border: 1px solid #ddd; padding: 8px;">Leg Activities</th>
                        <th class="ppp-coord-cell" style="border: 1px solid #ddd; padding: 8px;">End Coords</th>
                        <th class="ppp-activities-cell" style="border: 1px solid #ddd; padding: 8px;">Arrival Activities</th>
                        <th style="border: 1px solid #ddd; padding: 8px;">Actions</th>
                    </tr>
                </thead>
                <tbody>
        `;

        if (passagePlan.legs.length === 0) {
            tableHtml += `<tr><td colspan="6" style="text-align: center; padding: 1em;">No legs defined. Click 'Add Leg' or 'Import from Measure'.</td></tr>`;
        } else {
            passagePlan.legs.forEach((leg, index) => {
                const distanceNm = leg.startPosition.distanceTo(leg.endPosition) / 1852;
                let bearing = calculateBearing(leg.startPosition, leg.endPosition);

                let bearingSuffix = '°T';
                if (bearingRef === 'mag') {
                    bearing = (bearing - magVariation + 360) % 360;
                    bearingSuffix = '°M';
                }

                const timeHours = leg.speed > 0 ? distanceNm / leg.speed : 0;
                const timeMins = timeHours * 60;

                // Helper to generate coord inputs
                const renderCoordInputs = (pos: L.LatLng, field: 'startPosition' | 'endPosition') => {
                    const decDisplay = coordFormat === 'deg-dec' ? '' : 'display:none;';
                    let html = `
                        <div style="${decDisplay}">
                            <div class="ppp-coord-row">
                                <span class="ppp-coord-label">Lat:</span>
                                <input type="number" class="ppp-coord-input ppp-dec-lat" data-leg="${index}" data-field="${field}" data-sub="lat" value="${pos.lat.toFixed(6)}" step="0.000001">
                            </div>
                            <div class="ppp-coord-row">
                                <span class="ppp-coord-label">Lng:</span>
                                <input type="number" class="ppp-coord-input ppp-dec-lng" data-leg="${index}" data-field="${field}" data-sub="lng" value="${pos.lng.toFixed(6)}" step="0.000001">
                            </div>
                        </div>
                    `;

                    if (coordFormat === 'deg-min') {
                        const lat = toDegMin(pos.lat);
                        const lng = toDegMin(pos.lng);
                        html += `
                            <div class="ppp-coord-row">
                                <span class="ppp-coord-label">Lat:</span>
                                <input type="number" class="ppp-coord-input-small ppp-dms" data-leg="${index}" data-field="${field}" data-sub="latDeg" value="${lat.deg}">°
                                <input type="number" class="ppp-coord-input-small ppp-dms" data-leg="${index}" data-field="${field}" data-sub="latMin" value="${lat.min.toFixed(3)}" step="0.001">'
                            </div>
                            <div class="ppp-coord-row">
                                <span class="ppp-coord-label">Lng:</span>
                                <input type="number" class="ppp-coord-input-small ppp-dms" data-leg="${index}" data-field="${field}" data-sub="lngDeg" value="${lng.deg}">°
                                <input type="number" class="ppp-coord-input-small ppp-dms" data-leg="${index}" data-field="${field}" data-sub="lngMin" value="${lng.min.toFixed(3)}" step="0.001">'
                            </div>
                        `;
                    }
                    return html;
                };

                tableHtml += `
                    <tr data-leg-index="${index}">
                        <td class="ppp-coord-cell" style="border: 1px solid #ddd; padding: 8px;">
                            <strong>[${index}]</strong><br>
                            ${renderCoordInputs(leg.startPosition, 'startPosition')}
                        </td>
                        <td class="ppp-details-cell" style="border: 1px solid #ddd; padding: 8px;">
                            Course: ${bearing.toFixed(0)}${bearingSuffix}<br>
                            Speed: <span contenteditable="true" data-field="speed">${leg.speed.toFixed(0)}</span> kts<br>
                            Time: <span contenteditable="true" data-field="time">${timeMins.toFixed(0)}</span> mins<br>
                            Dist: ${distanceNm.toFixed(2)} NM
                        </td>
                        <td class="ppp-activities-cell" style="border: 1px solid #ddd; padding: 8px;">${renderActivities(leg.activitiesDuringLeg, index, 'activitiesDuringLeg')}</td>
                        <td class="ppp-coord-cell" style="border: 1px solid #ddd; padding: 8px;">
                            <strong>[${index + 1}]</strong><br>
                            ${renderCoordInputs(leg.endPosition, 'endPosition')}
                        </td>
                        <td class="ppp-activities-cell" style="border: 1px solid #ddd; padding: 8px;">${renderActivities(leg.activitiesAtEnd, index, 'activitiesAtEnd')}</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">
                            <button class="ppp-split-leg-btn" data-index="${index}" title="Split this leg into two">➗</button>
                            <button class="ppp-remove-leg-btn" data-index="${index}" title="Remove this leg">❌</button>
                        </td>
                    </tr>
                `;
            });
        }

        tableHtml += `</tbody></table>`;
        tableContainer.innerHTML = tableHtml;
        attachTableListeners();
    }

    function renderActivities(activities: ActivityInternal[], legIndex: number, position: 'activitiesDuringLeg' | 'activitiesAtEnd') {
        let html = activities.map((act, actIndex) => {
            let details = '';
            switch (act.type) {
                case 'depthSounding':
                    details = 'Depth:';
                    if (act.chartDatum !== undefined && act.chartDatum !== null && !isNaN(act.chartDatum)) details += ` Chart Datum: ${act.chartDatum}m`;
                    if (act.expectedDepth !== undefined && act.expectedDepth !== null && !isNaN(act.expectedDepth)) details += ` Expected ${act.expectedDepth}m`;
                    if (act.actualDepth !== undefined && act.actualDepth !== null && !isNaN(act.actualDepth)) details += ` Actual ${act.actualDepth}m`;
                    break;
                case 'radarFix':
                case 'handCompass':
                case 'transit':
                case 'beamCheck': {
                    let bearing = act.expectedBearing;
                    let suffix = '°T';
                    if (act.type === 'beamCheck') {
                        suffix = '°Rel';
                    } else if (bearingRef === 'mag') {
                        bearing = (bearing - magVariation + 360) % 360;
                        suffix = '°M';
                    }
                    const typeLabel = act.type === 'radarFix' ? 'Fix' : (act.type === 'transit' ? 'Transit' : (act.type === 'beamCheck' ? 'Beam' : 'Bearing'));
                    details = `${typeLabel}: ${act.target} @ ${bearing.toFixed(0)}${suffix}`;
                    if (act.expectedDistance) details += ` ${act.expectedDistance}NM`;

                    // Display Actuals if present (Transit has none)
                    if (act.type !== 'transit') {
                        if (act.type === 'beamCheck') {
                            if (act.actualTimeOffsetMinutes !== undefined) {
                                details += `<br><span style="color: blue;">Act Time: ${act.actualTimeOffsetMinutes.toFixed(2)}m</span>`;
                            }
                        } else if (act.actualBearing !== undefined) {
                            let actualBrg = act.actualBearing;
                            if (bearingRef === 'mag') {
                                actualBrg = (actualBrg - magVariation + 360) % 360;
                            }
                            details += `<br><span style="color: blue;">Act: ${actualBrg.toFixed(0)}${suffix}`;
                            if (act.actualDistance !== undefined) details += ` ${act.actualDistance}NM`;
                            details += `</span>`;
                        }
                    }
                    break;
                }
                case 'dangerBearing': {
                    const leg = passagePlan.legs[legIndex];
                    let origin = leg.startPosition;
                    if (position === 'activitiesDuringLeg' && act.timeOffsetMinutes !== undefined) {
                        const bearing = calculateBearing(leg.startPosition, leg.endPosition);
                        origin = calculatePositionAlongLeg(leg.startPosition, bearing, leg.speed, act.timeOffsetMinutes);
                    } else if (position === 'activitiesAtEnd') {
                        origin = leg.endPosition;
                    }

                    const distMeters = origin.distanceTo(act.hazardPosition);
                    let tangentBearing = 0;
                    if (distMeters > act.dangerDistanceMeters) {
                        const bearingToHazard = calculateBearing(origin, act.hazardPosition);
                        const alphaRad = Math.asin(act.dangerDistanceMeters / distMeters);
                        const alphaDeg = alphaRad * 180 / Math.PI;
                        if (act.side === 'p') {
                            tangentBearing = (bearingToHazard + alphaDeg) % 360;
                        } else {
                            tangentBearing = (bearingToHazard - alphaDeg + 360) % 360;
                        }
                    }

                    let displayBearing = tangentBearing;
                    let suffix = '°T';
                    if (bearingRef === 'mag') {
                        displayBearing = (displayBearing - magVariation + 360) % 360;
                        suffix = '°M';
                    }

                    let limitText = act.side === 'p' ? 'not below' : 'not above';
                    if (!act.name) {
                        limitText = limitText.charAt(0).toUpperCase() + limitText.slice(1);
                    }
                    const namePart = act.name ? `${act.name} ` : '';
                    details = `Danger (${act.side === 'p' ? 'Port' : 'Stbd'}): ${namePart}${limitText} ${displayBearing.toFixed(0)}${suffix}`;
                    break;
                }
                case 'clearingBearing': {
                    let pb = act.portBearing || 0;
                    let sb = act.starboardBearing || 0;
                    let suffix = '°T';
                    if (bearingRef === 'mag') {
                        pb = (pb - magVariation + 360) % 360;
                        sb = (sb - magVariation + 360) % 360;
                        suffix = '°M';
                    }

                    const portReciprocal = (Number(pb) + 180) % 360;
                    const stbdReciprocal = (Number(sb) + 180) % 360;
                    details = `Clearing: ${act.name || ''} Port NGT ${portReciprocal.toFixed(0)}${suffix}, Stbd NLT ${stbdReciprocal.toFixed(0)}${suffix}`;
                    break;
                }
            }
            if (act.timeOffsetMinutes !== undefined) {
                details += ` @ ${act.timeOffsetMinutes.toFixed(2)}m`;
            }
            return `<div class="activity-item" data-leg="${legIndex}" data-pos="${position}" data-act="${actIndex}" style="cursor: pointer; border: 1px solid #eee; padding: 2px; margin-bottom: 2px;">${details} <button class="ppp-remove-activity-btn" title="Remove activity" style="float: right;">x</button></div>`;
        }).join('');
        html += `<button class="ppp-add-activity-btn" data-leg="${legIndex}" data-pos="${position}">+ Add Activity</button>`;
        return html;
    }

    function attachTableListeners() {
        document.querySelectorAll('.ppp-remove-leg-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const index = parseInt((e.currentTarget as HTMLElement).dataset.index!);
                passagePlan.legs.splice(index, 1);
                if (index > 0 && index < passagePlan.legs.length) {
                    passagePlan.legs[index].startPosition = passagePlan.legs[index - 1].endPosition;
                }
                render();
            });
        });

        document.querySelectorAll('.ppp-split-leg-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const index = parseInt((e.currentTarget as HTMLElement).dataset.index!);
                const leg = passagePlan.legs[index];

                // Calculate midpoint
                const midLat = (leg.startPosition.lat + leg.endPosition.lat) / 2;
                const midLng = (leg.startPosition.lng + leg.endPosition.lng) / 2;
                const midPoint = L.latLng(midLat, midLng);

                // Create two new legs
                const leg1: LegInternal = {
                    startPosition: leg.startPosition,
                    endPosition: midPoint,
                    speed: leg.speed,
                    activitiesDuringLeg: leg.activitiesDuringLeg, // Keep during activities
                    activitiesAtEnd: [] // End activities move to the second leg
                };

                const leg2: LegInternal = {
                    startPosition: midPoint,
                    endPosition: leg.endPosition,
                    speed: leg.speed,
                    activitiesDuringLeg: [], // New during activities for the second leg
                    activitiesAtEnd: leg.activitiesAtEnd // Keep end activities
                };

                // Replace the original leg with the two new legs
                passagePlan.legs.splice(index, 1, leg1, leg2);
                render();
            });
        });

        // Handle Coordinate Inputs
        tableContainer.querySelectorAll('input.ppp-coord-input, input.ppp-coord-input-small').forEach(input => {
            input.addEventListener('change', (e) => {
                const target = e.currentTarget as HTMLInputElement;
                const legIndex = parseInt(target.dataset.leg!);
                const field = target.dataset.field as 'startPosition' | 'endPosition';
                const sub = target.dataset.sub!;

                // Handle DMS inputs by updating the hidden decimal input
                if (target.classList.contains('ppp-dms')) {
                    const row = target.closest('.ppp-coord-row')!;
                    const isLat = sub.startsWith('lat');

                    const degInput = row.querySelector(`[data-sub="${isLat ? 'latDeg' : 'lngDeg'}"]`) as HTMLInputElement;
                    const minInput = row.querySelector(`[data-sub="${isLat ? 'latMin' : 'lngMin'}"]`) as HTMLInputElement;

                    const deg = parseFloat(degInput.value);
                    const min = parseFloat(minInput.value);

                    if (!isNaN(deg) && !isNaN(min)) {
                        const decVal = fromDegMin(deg, min);
                        const decSub = isLat ? 'lat' : 'lng';
                        const decInput = tableContainer.querySelector(`input.ppp-dec-${decSub}[data-leg="${legIndex}"][data-field="${field}"]`) as HTMLInputElement;

                        if (decInput) {
                            decInput.value = decVal.toFixed(6);
                            decInput.dispatchEvent(new Event('change'));
                        }
                    }
                    return;
                }

                // Handle Decimal inputs (either visible or hidden)
                const leg = passagePlan.legs[legIndex];
                let currentPos = leg[field];
                const val = parseFloat(target.value);

                if (!isNaN(val)) {
                    if (sub === 'lat') currentPos = new L.LatLng(val, currentPos.lng);
                    if (sub === 'lng') currentPos = new L.LatLng(currentPos.lat, val);

                    leg[field] = currentPos;

                    // Link legs if needed
                    if (field === 'endPosition' && legIndex + 1 < passagePlan.legs.length) {
                        passagePlan.legs[legIndex + 1].startPosition = currentPos;
                    } else if (field === 'startPosition' && legIndex > 0) {
                        passagePlan.legs[legIndex - 1].endPosition = currentPos;
                    }

                    render();
                }
            });
        });

        tableContainer.querySelectorAll('[contenteditable="true"]').forEach(cell => {
            cell.addEventListener('blur', (e) => {
                const target = e.currentTarget as HTMLElement;
                const legIndex = parseInt((target.closest('tr') as HTMLTableRowElement).dataset.legIndex!);
                const field = target.dataset.field!;
                const leg = passagePlan.legs[legIndex];

                if (field === 'speed') {
                    const newSpeed = parseFloat(target.innerText);
                    if (!isNaN(newSpeed) && newSpeed > 0) {
                        const oldSpeed = leg.speed;
                        leg.speed = newSpeed;

                        // Recalculate time offsets to maintain geographic position
                        leg.activitiesDuringLeg.forEach(act => {
                            if (act.timeOffsetMinutes !== undefined) {
                                act.timeOffsetMinutes = act.timeOffsetMinutes * (oldSpeed / newSpeed);
                            }
                        });
                    }
                } else if (field === 'time') {
                    const newTimeMins = parseFloat(target.innerText);
                    if (!isNaN(newTimeMins) && newTimeMins > 0) {
                        const distanceNm = leg.startPosition.distanceTo(leg.endPosition) / 1852;
                        // speed = distance / time (hours)
                        leg.speed = distanceNm / (newTimeMins / 60);
                    }
                }
                render();
            });
        });

        document.querySelectorAll('.ppp-add-activity-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const { leg, pos } = (e.currentTarget as HTMLElement).dataset;
                editingActivity = { legIndex: parseInt(leg!), position: pos as 'activitiesDuringLeg' | 'activitiesAtEnd' };
                openActivityModal();
            });
        });

        document.querySelectorAll('.ppp-remove-activity-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const { leg, pos, act } = ((e.currentTarget as HTMLElement).closest('.activity-item') as HTMLElement).dataset;
                passagePlan.legs[parseInt(leg!)][pos as 'activitiesDuringLeg' | 'activitiesAtEnd'].splice(parseInt(act!), 1);
                render();
            });
        });

        // Add click listener for editing activities
        document.querySelectorAll('.activity-item').forEach(item => {
            item.addEventListener('click', (e) => {
                const { leg, pos, act } = (e.currentTarget as HTMLElement).dataset;
                editingActivity = {
                    legIndex: parseInt(leg!),
                    position: pos as 'activitiesDuringLeg' | 'activitiesAtEnd',
                    activityIndex: parseInt(act!)
                };
                openActivityModal();
            });
        });
    }

    function renderSavedPlans() {
        const items = listLocalStorageItems<any>('ppp');
        if (items.length === 0) {
            savedSection.style.display = 'none';
            return;
        }

        savedSection.style.display = 'block';
        savedList.innerHTML = '';

        items.forEach((item: StoredItem<any>) => {
            const div = document.createElement('div');
            div.style.cssText = 'margin-bottom:0.5em; padding:0.5em; border:1px solid #eee; display:flex; align-items:center; justify-content:space-between;';

            const timestamp = new Date(item.timestamp).toLocaleString();
            const infoSpan = document.createElement('span');
            infoSpan.innerHTML = `<strong>${item.name}</strong><br><small>${timestamp}</small>`;

            const btnContainer = document.createElement('div');
            const loadBtn = document.createElement('button');
            loadBtn.textContent = '✅ Load';
            loadBtn.style.marginRight = '0.5em';
            loadBtn.onclick = () => {
                const hydrated = mapPlanToInternal(item.data);
                if (hydrated) {
                    passagePlan = hydrated;
                    if (passagePlan.name) planNameInput.value = passagePlan.name;
                    render();
                } else {
                    alert("Failed to load plan: Invalid data");
                }
            };

            const deleteBtn = document.createElement('button');
            deleteBtn.textContent = '❌';
            deleteBtn.onclick = () => {
                if (confirm(`Are you sure you want to delete "${item.name}"?`)) {
                    deleteFromLocalStorage('ppp', item.name);
                    render(); // Re-render to update list
                }
            };

            btnContainer.appendChild(loadBtn);
            btnContainer.appendChild(deleteBtn);
            div.appendChild(infoSpan);
            div.appendChild(btnContainer);
            savedList.appendChild(div);
        });
    }

    function openActivityModal() {
        activityFormContainer.innerHTML = `
            <label>Type:
                <select id="ppp-activity-type">
                    <option value="depthSounding">Depth Sounding</option>
                    <option value="radarFix">Radar Fix</option>
                    <option value="handCompass">Hand Compass</option>
                    <option value="transit">Transit</option>
                    <option value="beamCheck">Beam Check</option>
                    <option value="dangerBearing">Danger Bearing</option>
                    <option value="clearingBearing">Clearing Bearing</option>
                </select>
            </label>
            <div id="ppp-activity-fields" style="margin-top: 1em;"></div>
        `;
        activityModal.style.display = 'block';

        const typeSelect = document.getElementById('ppp-activity-type') as HTMLSelectElement;
        typeSelect.onchange = () => {
            if (editingActivity) {
                renderActivityFields(typeSelect.value, editingActivity.position);
            }
        };

        if (editingActivity) {
            // Pre-fill if editing existing activity
            if (editingActivity.activityIndex !== undefined) {
                const act = passagePlan.legs[editingActivity.legIndex][editingActivity.position][editingActivity.activityIndex];
                typeSelect.value = act.type;
                renderActivityFields(act.type, editingActivity.position, act);
            } else {
                renderActivityFields(typeSelect.value, editingActivity.position);
            }
        }
    }

    function renderActivityFields(type: string, position: string, existingData?: ActivityInternal) {
        const fieldsContainer = document.getElementById('ppp-activity-fields')!;
        let fieldsHtml = '';

        // Helper to safely get value
        const val = (key: keyof any) => existingData ? (existingData as any)[key] || '' : '';

        // Helper to get bearing value in correct format (True/Mag)
        const getBearingVal = (key: 'expectedBearing' | 'actualBearing' | 'portBearing' | 'starboardBearing') => {
            if (!existingData) return '';
            let b = (existingData as any)[key];
            if (b === undefined || b === '') return '';
            if (bearingRef === 'mag') {
                b = (b - magVariation + 360) % 360;
            }
            return b.toFixed(1); // Display with 1 decimal place
        };

        const bearingSuffix = bearingRef === 'mag' ? '°M' : '°T';

        switch (type) {
            case 'depthSounding': {
                fieldsHtml = `
                    <div style="margin-bottom: 0.5em;"><label>Expected (m): <input type="number" id="act-expectedDepth" placeholder="Expected (m)" class="ppp-input" value="${val('expectedDepth')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Chart Datum (m): <input type="number" id="act-chartDatum" placeholder="CD (m)" class="ppp-input" value="${val('chartDatum')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Actual (m): <input type="number" id="act-actualDepth" placeholder="Actual (m)" class="ppp-input" value="${val('actualDepth')}"></label></div>
                `;
                break;
            }
            case 'radarFix': {
                fieldsHtml = `
                    <div style="margin-bottom: 0.5em;"><label>Target Name: <input type="text" id="act-target" placeholder="Target Name" class="ppp-input" value="${val('target')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Expected Bearing (${bearingSuffix}): <input type="number" id="act-expectedBearing" placeholder="Expected Bearing (${bearingSuffix})" class="ppp-input" value="${getBearingVal('expectedBearing')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Expected Distance (NM): <input type="number" id="act-expectedDistance" placeholder="Expected Distance (NM)" class="ppp-input" value="${existingData ? val('expectedDistance') : '1'}"></label></div>
                    <hr style="margin: 1em 0;">
                    <label>Actuals (Optional):</label>
                    <div style="margin-bottom: 0.5em;"><label>Actual Bearing (${bearingSuffix}): <input type="number" id="act-actualBearing" placeholder="Actual Bearing (${bearingSuffix})" class="ppp-input" value="${getBearingVal('actualBearing')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Actual Distance (NM): <input type="number" id="act-actualDistance" placeholder="Actual Distance (NM)" class="ppp-input" value="${val('actualDistance')}"></label></div>
                `;
                break;
            }
            case 'handCompass': {
                fieldsHtml = `
                    <div style="margin-bottom: 0.5em;"><label>Target Name: <input type="text" id="act-target" placeholder="Target Name" class="ppp-input" value="${val('target')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Expected Bearing (${bearingSuffix}): <input type="number" id="act-expectedBearing" placeholder="Expected Bearing (${bearingSuffix})" class="ppp-input" value="${getBearingVal('expectedBearing')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Expected Distance (NM): <input type="number" id="act-expectedDistance" placeholder="Expected Distance (NM)" class="ppp-input" value="${existingData ? val('expectedDistance') : '1'}"></label></div>
                    <hr style="margin: 1em 0;">
                    <label>Actuals (Optional):</label>
                    <div style="margin-bottom: 0.5em;"><label>Actual Bearing (${bearingSuffix}): <input type="number" id="act-actualBearing" placeholder="Actual Bearing (${bearingSuffix})" class="ppp-input" value="${getBearingVal('actualBearing')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Actual Distance (NM): <input type="number" id="act-actualDistance" placeholder="Actual Distance (NM)" class="ppp-input" value="${val('actualDistance')}"></label></div>
                `;
                break;
            }
            case 'transit': {
                fieldsHtml = `
                    <div style="margin-bottom: 0.5em;"><label>Target Name: <input type="text" id="act-target" placeholder="Target Name" class="ppp-input" value="${val('target')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Expected Bearing (${bearingSuffix}): <input type="number" id="act-expectedBearing" placeholder="Expected Bearing (${bearingSuffix})" class="ppp-input" value="${getBearingVal('expectedBearing')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Expected Distance (NM): <input type="number" id="act-expectedDistance" placeholder="Expected Distance (NM)" class="ppp-input" value="${existingData ? val('expectedDistance') : '1'}"></label></div>
                `;
                break;
            }
            case 'beamCheck': {
                fieldsHtml = `
                    <div style="margin-bottom: 0.5em;"><label>Target Name: <input type="text" id="act-target" placeholder="Target Name" class="ppp-input" value="${val('target')}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Relative Bearing (°Rel): <input type="number" id="act-expectedBearing" placeholder="Relative Bearing (°Rel)" class="ppp-input" value="${existingData ? val('expectedBearing') : '90'}"></label></div>
                    <div style="margin-bottom: 0.5em;"><label>Expected Distance (NM): <input type="number" id="act-expectedDistance" placeholder="Expected Distance (NM)" class="ppp-input" value="${existingData ? val('expectedDistance') : '1'}"></label></div>
                    <hr style="margin: 1em 0;">
                    <label>Actuals (Optional):</label>
                    <div style="margin-bottom: 0.5em;"><label>Actual Time Offset (mins): <input type="number" id="act-actualTimeOffset" placeholder="Actual Time Offset (mins)" class="ppp-input" value="${val('actualTimeOffsetMinutes')}"></label></div>
                `;
                break;
            }
            case 'dangerBearing': {
                const side = val('side') || 'p';
                const hazardPos = existingData && (existingData as DangerBearingActivityInternal).hazardPosition
                    ? (existingData as DangerBearingActivityInternal).hazardPosition
                    : map.getCenter();

                fieldsHtml = `
                    <div style="margin-bottom: 0.5em;"><label>Name: <input type="text" id="act-name" placeholder="Name (e.g. Crusoe Island)" class="ppp-input" value="${val('name')}"></label></div>
                    <div style="margin-bottom: 0.5em;">
                        <label><input type="radio" name="act-side" value="p" ${side === 'p' ? 'checked' : ''}> Port</label>
                        <label><input type="radio" name="act-side" value="s" ${side === 's' ? 'checked' : ''}> Starboard</label>
                    </div>
                    ${renderCoordInputGroup('act-hazard', 'Hazard Position', hazardPos, coordFormat)}
                    <label>Danger Dist: <input type="number" id="act-dangerDistance" placeholder="Meters" class="ppp-input" value="${val('dangerDistanceMeters')}"></label>
                `;
                break;
            }
            case 'clearingBearing': {
                const targetPos = existingData && (existingData as ClearingBearingActivityInternal).targetPosition
                    ? (existingData as ClearingBearingActivityInternal).targetPosition
                    : map.getCenter();

                const pb = existingData ? getBearingVal('portBearing') : '';
                const sb = existingData ? getBearingVal('starboardBearing') : '';
                const pl = val('portLength') || 1;
                const sl = val('starboardLength') || 1;

                const portReciprocal = (Number(pb) + 180) % 360;
                const stbdReciprocal = (Number(sb) + 180) % 360;
                let suffix = '°T';
                if (bearingRef === 'mag') {
                    suffix = '°M';
                }

                const details = `Clearing: ${val('name')}\nPort NGT ${portReciprocal.toFixed(0)}${suffix}\nStbd NLT ${stbdReciprocal.toFixed(0)}${suffix}`;

                fieldsHtml = `
                    <div style="margin-bottom: 0.5em;"><label>Name: <input type="text" id="act-name" placeholder="Name" class="ppp-input" value="${val('name')}"></label></div>
                    ${renderCoordInputGroup('act-target', 'Target', targetPos, coordFormat)}
                    <div style="margin-bottom: 0.5em;">
                        <strong>Port Line:</strong><br>
                        <label>Bearing (${bearingSuffix}): <input type="number" id="act-portBearing" class="ppp-input" value="${pb}"></label>
                        <label>Length (NM): <input type="number" id="act-portLength" class="ppp-input" value="${pl}"></label>
                    </div>
                    <div style="margin-bottom: 0.5em;">
                        <strong>Starboard Line:</strong><br>
                        <label>Bearing (${bearingSuffix}): <input type="number" id="act-stbdBearing" class="ppp-input" value="${sb}"></label>
                        <label>Length (NM): <input type="number" id="act-stbdLength" class="ppp-input" value="${sl}"></label>
                    </div>
                    <div style="margin-bottom: 0.5em;  resize: none; width: 100%;">
                        <strong>Instructions:</strong><br>
                        <textarea disabled rows="3" style="width: 100%; box-sizing: border-box;" id="ppp-clearing-details">${details}</textarea>
                    </div>
                `;
                break;
            }
        }

        if (position === 'activitiesDuringLeg') {
            const tm = val('timeOffsetMinutes');
            const tmFormatted = typeof tm === 'number' ? tm.toFixed(2) : tm;
            fieldsHtml += `<div style="margin-top: 0.5em;"><label>Time Offset (mins): <input type="number" id="act-timeOffset" placeholder="Minutes along leg" class="ppp-input" value="${tmFormatted}"></label></div>`;
        }

        fieldsContainer.innerHTML = fieldsHtml;

        if (type === 'clearingBearing') {
            const nameInput = document.getElementById('act-name');
            const portBearingInput = document.getElementById('act-portBearing');
            const stbdBearingInput = document.getElementById('act-stbdBearing');
            const detailsTextarea = document.getElementById('ppp-clearing-details');

            if (nameInput && portBearingInput && stbdBearingInput && detailsTextarea) {
                const updateDetails = () => {
                    const name = (nameInput as HTMLInputElement).value;
                    const pb = parseFloat((portBearingInput as HTMLInputElement).value) || 0;
                    const sb = parseFloat((stbdBearingInput as HTMLInputElement).value) || 0;

                    const portReciprocal = (pb + 180) % 360;
                    const stbdReciprocal = (sb + 180) % 360;
                    const suffix = bearingRef === 'mag' ? '°M' : '°T';

                    (detailsTextarea as HTMLTextAreaElement).value = `Clearing: ${name}\nPort NGT ${portReciprocal.toFixed(0)}${suffix}\nStbd NLT ${stbdReciprocal.toFixed(0)}${suffix}`;
                };

                nameInput.addEventListener('input', updateDetails);
                portBearingInput.addEventListener('input', updateDetails);
                stbdBearingInput.addEventListener('input', updateDetails);
            }
        }

        // Add listeners for DMS inputs in modal
        const dmsInputs = fieldsContainer.querySelectorAll('.ppp-modal-dms');
        dmsInputs.forEach(input => {
            input.addEventListener('input', (e) => {
                const target = e.target as HTMLInputElement;
                const targetId = target.dataset.target!;

                const degInput = fieldsContainer.querySelector(`.ppp-modal-dms[data-target="\${targetId}"][data-type="deg"]`) as HTMLInputElement;
                const minInput = fieldsContainer.querySelector(`.ppp-modal-dms[data-target="\${targetId}"][data-type="min"]`) as HTMLInputElement;

                if (degInput && minInput) {
                    const deg = parseFloat(degInput.value);
                    const min = parseFloat(minInput.value);
                    if (!isNaN(deg) && !isNaN(min)) {
                        const decVal = fromDegMin(deg, min);
                        const decInput = document.getElementById(targetId) as HTMLInputElement;
                        if (decInput) {
                            decInput.value = decVal.toFixed(6);
                        }
                    }
                }
            });
        });
    }

    function saveActivity() {
        if (!editingActivity) return;
        const type = (document.getElementById('ppp-activity-type') as HTMLSelectElement).value;
        let newActivity: ActivityInternal | null = null;

        // Helper to parse bearing input back to True degrees
        const parseBearing = (inputId: string): number => {
            const val = parseFloat((document.getElementById(inputId) as HTMLInputElement).value);
            if (isNaN(val)) return NaN;
            if (bearingRef === 'mag') {
                return (val + magVariation) % 360;
            }
            return val;
        };

        try {
            switch (type) {
                case 'depthSounding': {
                    newActivity = {
                        type: 'depthSounding',
                        expectedDepth: parseFloat((document.getElementById('act-expectedDepth') as HTMLInputElement).value)
                    };
                    const actChartDatum = parseFloat((document.getElementById('act-chartDatum') as HTMLInputElement).value);
                    if (!isNaN(actChartDatum)) newActivity.chartDatum = actChartDatum;
                    const actActualDepth = parseFloat((document.getElementById('act-actualDepth') as HTMLInputElement).value);
                    if (!isNaN(actActualDepth)) newActivity.actualDepth = actActualDepth;
                    break;
                }
                case 'radarFix': {
                    newActivity = {
                        type: 'radarFix',
                        target: (document.getElementById('act-target') as HTMLInputElement).value,
                        expectedBearing: parseBearing('act-expectedBearing'),
                        expectedDistance: parseFloat((document.getElementById('act-expectedDistance') as HTMLInputElement).value)
                    };
                    const actBrgRadar = parseBearing('act-actualBearing');
                    const actDistRadar = (document.getElementById('act-actualDistance') as HTMLInputElement).value;
                    if (!isNaN(actBrgRadar)) newActivity.actualBearing = actBrgRadar;
                    if (actDistRadar) newActivity.actualDistance = parseFloat(actDistRadar);
                    break;
                }
                case 'handCompass': {
                    newActivity = {
                        type: 'handCompass',
                        target: (document.getElementById('act-target') as HTMLInputElement).value,
                        expectedBearing: parseBearing('act-expectedBearing'),
                        expectedDistance: parseFloat((document.getElementById('act-expectedDistance') as HTMLInputElement).value)
                    };
                    const actBrgCompass = parseBearing('act-actualBearing');
                    const actDistCompass = (document.getElementById('act-actualDistance') as HTMLInputElement).value;
                    if (!isNaN(actBrgCompass)) newActivity.actualBearing = actBrgCompass;
                    if (actDistCompass) newActivity.actualDistance = parseFloat(actDistCompass);
                    break;
                }
                case 'transit': {
                    newActivity = {
                        type: 'transit',
                        target: (document.getElementById('act-target') as HTMLInputElement).value,
                        expectedBearing: parseBearing('act-expectedBearing'),
                        expectedDistance: parseFloat((document.getElementById('act-expectedDistance') as HTMLInputElement).value)
                    };
                    break;
                }
                case 'beamCheck': {
                    newActivity = {
                        type: 'beamCheck',
                        target: (document.getElementById('act-target') as HTMLInputElement).value,
                        expectedBearing: parseFloat((document.getElementById('act-expectedBearing') as HTMLInputElement).value), // Relative bearing, no mag conversion
                        expectedDistance: parseFloat((document.getElementById('act-expectedDistance') as HTMLInputElement).value)
                    };
                    const actTimeOffset = parseFloat((document.getElementById('act-actualTimeOffset') as HTMLInputElement).value);
                    if (!isNaN(actTimeOffset)) newActivity.actualTimeOffsetMinutes = actTimeOffset;
                    break;
                }
                case 'dangerBearing': {
                    const side = (document.querySelector('input[name="act-side"]:checked') as HTMLInputElement).value as 'p' | 's';
                    const lat = parseFloat((document.getElementById('act-hazardLat') as HTMLInputElement).value);
                    const lng = parseFloat((document.getElementById('act-hazardLng') as HTMLInputElement).value);
                    const dist = parseFloat((document.getElementById('act-dangerDistance') as HTMLInputElement).value);
                    const name = (document.getElementById('act-name') as HTMLInputElement).value;

                    newActivity = {
                        type: 'dangerBearing',
                        side: side,
                        hazardPosition: new L.LatLng(lat, lng),
                        dangerDistanceMeters: dist
                    };
                    if (name) newActivity.name = name;
                    break;
                }
                case 'clearingBearing': {
                    const tLat = parseFloat((document.getElementById('act-targetLat') as HTMLInputElement).value);
                    const tLng = parseFloat((document.getElementById('act-targetLng') as HTMLInputElement).value);
                    const pb = parseBearing('act-portBearing');
                    const pl = parseFloat((document.getElementById('act-portLength') as HTMLInputElement).value);
                    const sb = parseBearing('act-stbdBearing');
                    const sl = parseFloat((document.getElementById('act-stbdLength') as HTMLInputElement).value);
                    const cbName = (document.getElementById('act-name') as HTMLInputElement).value;

                    newActivity = {
                        type: 'clearingBearing',
                        targetPosition: new L.LatLng(tLat, tLng),
                        portBearing: pb || 0,
                        portLength: parseFloat(pl.toFixed(2)),
                        starboardBearing: sb || 0,
                        starboardLength: parseFloat(sl.toFixed(2))
                    };
                    if (cbName) newActivity.name = cbName;
                    break;
                }
            }

            if (newActivity && editingActivity.position === 'activitiesDuringLeg') {
                const timeOffsetInput = document.getElementById('act-timeOffset') as HTMLInputElement;
                if (timeOffsetInput && timeOffsetInput.value) {
                    newActivity.timeOffsetMinutes = parseFloat(timeOffsetInput.value);
                } else {
                    if (newActivity.type === 'dangerBearing' || newActivity.type === 'clearingBearing') {
                        newActivity.timeOffsetMinutes = 0;
                    } else {
                        const leg = passagePlan.legs[editingActivity.legIndex];
                        const distanceNm = leg.startPosition.distanceTo(leg.endPosition) / 1852;
                        const timeHours = leg.speed > 0 ? distanceNm / leg.speed : 0;
                        newActivity.timeOffsetMinutes = (timeHours * 60) / 2;
                    }
                }
            }

        } catch (e) { console.error("Error parsing activity data:", e); }

        if (newActivity) {
            const { legIndex, position, activityIndex } = editingActivity;
            if (activityIndex !== undefined) {
                // Update existing
                passagePlan.legs[legIndex][position][activityIndex] = newActivity;
            } else {
                // Add new
                passagePlan.legs[legIndex][position].push(newActivity);
            }
        }

        closeActivityModal();
        render();
    }

    function closeActivityModal() {
        activityModal.style.display = 'none';
        editingActivity = null;
    }

    // --- Settings Listeners ---
    coordFormatRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
            coordFormat = (e.target as HTMLInputElement).value as 'deg-min' | 'deg-dec';
            render();
        });
    });

    bearingRefRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
            bearingRef = (e.target as HTMLInputElement).value as 'true' | 'mag';
            if (magVarInput) magVarInput.disabled = bearingRef !== 'mag';
            render();
        });
    });

    if (magVarInput) {
        magVarInput.addEventListener('change', (e) => {
            magVariation = parseFloat((e.target as HTMLInputElement).value) || 0;
            render();
        });
    }

    // --- Event Handlers ---
    addLegBtn.addEventListener('click', () => {
        const lastLeg = passagePlan.legs[passagePlan.legs.length - 1];
        const newStartPos = lastLeg ? lastLeg.endPosition : map.getCenter();

        const newLeg: LegInternal = {
            startPosition: newStartPos.clone(),
            // Changed offset to 0.002 as requested
            endPosition: L.latLng(newStartPos.lat - 0.002, newStartPos.lng + 0.002),
            speed: 5,
            activitiesDuringLeg: [],
            activitiesAtEnd: []
        };
        passagePlan.legs.push(newLeg);
        render();
    });

    importMeasureBtn.addEventListener('click', () => {
        if (lastMeasuredPolyline && lastMeasuredPolyline.length >= 2) {
            passagePlan.legs = [];
            for (let i = 0; i < lastMeasuredPolyline.length - 1; i++) {
                passagePlan.legs.push({
                    startPosition: lastMeasuredPolyline[i],
                    endPosition: lastMeasuredPolyline[i + 1],
                    speed: 5, // Default speed
                    activitiesDuringLeg: [],
                    activitiesAtEnd: []
                });
            }
            render();
            importMeasureBtn.style.borderColor = ''; // Reset style
            importMeasureBtn.style.fontWeight = 'normal';
        } else {
            alert("No measured line captured. Use the measure tool (ruler icon), draw a route, and click the 'Finish' button on the measurement before importing.");
        }
    });

    planNameInput.addEventListener('input', (e) => {
        passagePlan.name = (e.target as HTMLInputElement).value;
        if (onPlanChange) {
            const encoded = encodePassagePlan(passagePlan);
            onPlanChange(encoded);
        }
    });

    saveBtn.addEventListener('click', () => {
        const name = prompt("Enter a name for this plan:", passagePlan.name || "");
        if (name) {
            passagePlan.name = name;
            planNameInput.value = name;
            saveToLocalStorage('ppp', name, mapPlanToExternal(passagePlan));
            render(); // to update saved list
        }
    });

    clearBtn.addEventListener('click', () => {
        if (confirm("Are you sure you want to clear the current passage plan? This will remove it from the map and reset the planner.")) {
            // Clear the passagePlan data
            passagePlan = { legs: [] };
            planNameInput.value = '';

            // Remove the 'ppp' parameter from the URL
            const url = new URL(window.location.href);
            url.searchParams.delete('ppp');
            window.history.replaceState({}, document.title, url.toString());

            // Re-render to clear the UI and map
            render();
        }
    });

    showBtn.addEventListener('click', () => {
        if (passagePlan.legs.length > 0) {
            const leg = passagePlan.legs[0];
            map.panTo(new LatLng(leg.startPosition.lat, leg.startPosition.lng));

        }
    });

    saveActivityBtn.addEventListener('click', saveActivity);
    cancelActivityBtn.addEventListener('click', closeActivityModal);
    closeBtn.addEventListener('click', togglePassagePlanner);
    printBtn.addEventListener('click', () => window.print());

    // --- Init ---
    // Removed addPppButton function call as it is now handled by the Coastguard panel

    map.getContainer().addEventListener('open-passage-planner-panel', () => {
        togglePassagePlanner();
    });

    map.getContainer().addEventListener('close-other-panels', (e: any) => {
        if (e.detail !== 'passage-planner-panel') {
            passagePlannerPanel.style.display = 'none';
        }
    });

    // @ts-ignore
    map.on('polylinemeasure:finish', (e: any) => {
        // Based on console.dir(e), the coordinates are in e.circleCoords
        if (e.circleCoords && e.circleCoords.length > 0) {
            lastMeasuredPolyline = e.circleCoords; // These are already LatLng objects
            importMeasureBtn.style.borderColor = 'green';
            importMeasureBtn.style.fontWeight = 'bold';
            alert('Measured line captured. You can now click "Import from Measure".');
        }
    });

    // Initial render if plan was loaded
    if (passagePlan.legs.length > 0) {
        render();
    }
}